import React, { useContext, useState, useEffect, memo } from 'react';

import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Form from '@smart-tv/components/form/form';
import Button from '@smart-tv/components/button/button';
import Input from '@smart-tv/components/input';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import useForm from '@smart-tv/hooks/use-form';
import LoginContext from '@smart-tv/pages/login/login-context';
import resetPassword from '@smart-tv/utils/kmm/requestPasswordChange';
import isEmail from '@smart-tv/pages/login/is-email';
import { ERROR_MESSAGES } from '@smart-tv/pages/login/constants';
import { networkError } from '@smart-tv/contexts/error-context/error-context';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import styles from '../login.module.scss';
import LoginError from '../login-content/login-error';

const FORGOT_PASSWORD_FOCUS_KEY = 'LoginFormForgotPasswordSendFocusKey';

interface Props {
  display: boolean;
  onSubmit: (email: string) => void;
  forgotPwText: string | undefined;
  onClose: () => void;
}

export const LoginForgotPasswordDialogForm = (props: Props) => {
  const { display, onSubmit, forgotPwText, onClose } = props;
  const { email } = useContext(LoginContext);
  const [errorMsg, setErrorMsg] = useState<string>();

  const { focusKey } = useFocusable({ isFocusBoundary: true });

  const resetPasswordForm = useForm({
    initialValues: { resetPasswordEmail: email },

    onSubmit: async ({ resetPasswordEmail }) => {
      if (!isEmail(resetPasswordEmail)) {
        return setErrorMsg(
          'Den indtastede e-mail er ikke gyldig. Tjek om du har skrevet rigtigt og prøv igen.'
        );
      }
      if (submitting) return;
      try {
        await resetPassword(resetPasswordEmail);
        onSubmit(resetPasswordEmail);
      } catch (error) {
        if (error === networkError) {
          setErrorMsg(ERROR_MESSAGES.NO_NETWORK);
          return;
        }
        onSubmit(resetPasswordEmail);
      }
    }
  });

  const { handleSubmit, getFieldProps, submitting } = resetPasswordForm;

  useEffect(() => {
    if (display) {
      setFocus(FORGOT_PASSWORD_FOCUS_KEY);
    }
  }, [display]);

  if (!display) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <Typography
        className={styles['login-forgot-password-dialog__title']}
        variant="h3"
        data-testid={testIds.login.forgotPasswordDialog.header}
      >
        Glemt adgangskode?
      </Typography>
      <Typography
        className={styles['login-forgot-password-dialog__description']}
        variant="body"
        data-testid={testIds.login.forgotPasswordDialog.text}
      >
        {forgotPwText ??
          'Indtast din e-mail for at bestille en ny adgangskode. Herefter modtager du en e-mail med vejledning til at nulstille din adgangskode.'}
      </Typography>
      <Form onSubmit={handleSubmit} validate={false} className={`${styles['login-form__form']} ${styles['login-form__form--offset-top']}`}>
        <Input
          className={styles['login-forgot-password-dialog__fieldset']}
          type="email"
          {...getFieldProps('resetPasswordEmail')}
          placeholder="Indtast e-mail"
          inputProps={{ 'data-testid': testIds.login.forgotPasswordDialog.email }}
        />
        <LoginError
          errorMsg={errorMsg}
          data-testid={testIds.login.forgotPasswordDialog.errorMessage}
        />
        <Button
          className={styles['login-forgot-password-dialog__accept-button']}
          type="submit"
          focusKey={FORGOT_PASSWORD_FOCUS_KEY}
          data-testid={testIds.login.forgotPasswordDialog.sendMail}
        >
          Send
        </Button>
        {IS_BROWSER && (
          <Button
            onClick={onClose}
            className={styles['login-forgot-password-dialog__back-button']}
            data-testid={testIds.login.forgotPasswordDialog.backButton}
          >
            Tilbage
          </Button>
        )}
      </Form>
    </FocusContext.Provider>
  );
};

export default memo(LoginForgotPasswordDialogForm);
